import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, 
  CheckCircle2, 
  FileSpreadsheet, 
  Users, 
  MessageSquare, 
  Smile,
  Calendar,
  TrendingUp,
  Download,
  ChevronDown,
  ChevronUp,
  Database,
  Edit2,
  Save,
  X,
  Loader2,
  Trash2,
  RotateCcw
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { downloadExcel, getTimestamp } from '../../utils/exportUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagerEditor = ({ plan, isDark, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [managerName, setManagerName] = useState(plan.manager === 'Unknown' ? '' : plan.manager);
  const [status, setStatus] = useState('idle'); // idle, saving, success, error

  const handleSave = async () => {
    if (!managerName.trim()) return;
    
    setStatus('saving');
    try {
      await axios.post(`${API}/plans/${encodeURIComponent(plan.planNumber)}/manager`, {
        manager: managerName
      });
      setStatus('success');
      setTimeout(() => {
        setIsEditing(false);
        setStatus('idle');
        onUpdate(); // Trigger refresh
      }, 1500);
    } catch (error) {
      console.error('Failed to update manager:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={managerName}
          onChange={(e) => setManagerName(e.target.value)}
          placeholder="Enter Manager Name"
          className={`px-2 py-1 text-xs rounded border outline-none w-32 transition-all
            ${isDark 
              ? 'bg-[#1a1a1c] border-white/20 text-white focus:border-emerald-500' 
              : 'bg-white border-slate-300 text-slate-900 focus:border-emerald-500'
            }`}
          autoFocus
        />
        {status === 'saving' ? (
          <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
        ) : status === 'success' ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : (
          <>
            <button 
              onClick={handleSave}
              className="p-1 hover:bg-emerald-500/10 rounded text-emerald-500 transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => setIsEditing(false)}
              className="p-1 hover:bg-red-500/10 rounded text-red-500 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-2">
      <span className={`${plan.manager === 'Unknown' ? 'text-red-500 font-medium' : (isDark ? 'text-slate-400' : 'text-slate-600')}`}>
        {plan.manager}
      </span>
      <button 
        onClick={() => setIsEditing(true)}
        className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded
          ${isDark ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}
        title="Edit Manager"
      >
        <Edit2 className="w-3 h-3" />
      </button>
    </div>
  );
};

const DataQualityReport = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [excludedPlans, setExcludedPlans] = useState([]);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const [reportRes, excludedRes] = await Promise.all([
        axios.get(`${API}/data-quality`),
        axios.get(`${API}/excluded-plans`),
      ]);
      setReport(reportRes.data);
      setExcludedPlans(excludedRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data quality report:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-500';
    if (score >= 70) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return isDark ? 'bg-emerald-500/10' : 'bg-emerald-100';
    if (score >= 70) return isDark ? 'bg-amber-500/10' : 'bg-amber-100';
    return isDark ? 'bg-red-500/10' : 'bg-red-100';
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleExportReport = async () => {
    await downloadExcel('clients', `knight_data_quality_${getTimestamp()}.xlsx`);
  };

  const toggleSelectPlan = (planNumber) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(planNumber)) next.delete(planNumber);
      else next.add(planNumber);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/excluded-plans`, { planNumbers: [...selectedForDelete] });
      setSelectedForDelete(new Set());
      await fetchReport();
    } catch (error) {
      console.error('Failed to exclude plans:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestorePlan = async (planNumber) => {
    try {
      await axios.delete(`${API}/excluded-plans/${encodeURIComponent(planNumber)}`);
      await fetchReport();
    } catch (error) {
      console.error('Failed to restore plan:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className={`p-8 rounded-2xl border text-center ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
        <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>No Data Available</h3>
        <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Upload data files to see the quality report
        </p>
      </div>
    );
  }

  const { summary, gaps, counts, sources } = report;

  const gapSections = [
    { 
      key: 'unknownManager', 
      label: 'Unknown Managers', 
      icon: Users, 
      color: 'text-red-500',
      description: 'Plans without assigned manager in any data source',
      data: gaps.unknownManager,
      count: counts.unknownManager
    },
    {
      key: 'unmappedRevenueTracker',
      label: 'Unmapped in Revenue Tracker',
      icon: TrendingUp,
      color: 'text-orange-500',
      description: `Plans in the Revenue Tracker workbook with no master-list mapping — revenue is EXCLUDED from all Revenue Tracker rollups until mapped (${((summary?.unmappedRevenueTrackerRevenue || 0) / 1000).toFixed(0)}k withheld)`,
      data: gaps.unmappedRevenueTracker || [],
      count: counts.unmappedRevenueTracker || 0,
      revenueField: 'revenue',
      extraField: 'fy',
    },
    { 
      key: 'missingCompliance', 
      label: 'Missing Compliance Data', 
      icon: FileSpreadsheet, 
      color: 'text-amber-500',
      description: 'Plans not found in Plan Status CSV',
      data: gaps.missingCompliance,
      count: counts.missingCompliance
    },
    { 
      key: 'missingWorkload', 
      label: 'Missing Workload Data', 
      icon: MessageSquare, 
      color: 'text-blue-500',
      description: 'Plans not found in Workload CSV',
      data: gaps.missingWorkload,
      count: counts.missingWorkload
    },
    { 
      key: 'missingMeetings', 
      label: 'Missing Meeting Data', 
      icon: Calendar, 
      color: 'text-purple-500',
      description: 'Plans not found in Meetings CSV',
      data: gaps.missingMeetings,
      count: counts.missingMeetings
    },
    { 
      key: 'missingNps', 
      label: 'Missing NPS Data', 
      icon: Smile, 
      color: 'text-cyan-500',
      description: 'Plans not found in NPS CSV',
      data: gaps.missingNps,
      count: counts.missingNps
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Database className="w-6 h-6 mr-3 text-emerald-500" />
            Data Quality Report
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            Analysis of data completeness across all uploaded CSV files
          </p>
        </div>
        <button
          onClick={handleExportReport}
          data-testid="export-quality-report"
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all
            ${isDark 
              ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20' 
              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
            }`}
        >
          <Download className="w-4 h-4" />
          Export Full Report
        </button>
      </div>

      {/* Overall Score Card */}
      <div className={`p-6 rounded-2xl border ${isDark ? 'bg-gradient-to-r from-emerald-500/10 via-transparent to-transparent border-emerald-500/20' : 'bg-gradient-to-r from-emerald-50 via-white to-white border-emerald-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Overall Data Quality Score</p>
            <div className="flex items-baseline gap-3 mt-1">
              <span className={`text-5xl font-bold ${getScoreColor(summary.overallScore)}`}>
                {summary.overallScore}%
              </span>
              <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {summary.completePlans} of {summary.totalPlans} plans fully complete
              </span>
            </div>
          </div>
          <div className={`p-4 rounded-2xl ${getScoreBg(summary.overallScore)}`}>
            {summary.overallScore >= 90 ? (
              <CheckCircle2 className={`w-10 h-10 ${getScoreColor(summary.overallScore)}`} />
            ) : (
              <AlertTriangle className={`w-10 h-10 ${getScoreColor(summary.overallScore)}`} />
            )}
          </div>
        </div>
      </div>

      {/* Coverage Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <CoverageCard 
          label="Manager Coverage" 
          value={summary.managerCoverage} 
          icon={Users}
          isDark={isDark}
        />
        <CoverageCard 
          label="Compliance Data" 
          value={summary.complianceCoverage} 
          icon={FileSpreadsheet}
          isDark={isDark}
        />
        <CoverageCard 
          label="Workload Data" 
          value={summary.workloadCoverage} 
          icon={MessageSquare}
          isDark={isDark}
        />
        <CoverageCard 
          label="Meeting Data" 
          value={summary.meetingsCoverage} 
          icon={Calendar}
          isDark={isDark}
        />
        <CoverageCard 
          label="NPS Data" 
          value={summary.npsCoverage} 
          icon={Smile}
          isDark={isDark}
        />
      </div>

      {/* Source Record Counts */}
      <div className={`p-5 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Records per Data Source
        </h3>
        <div className="grid grid-cols-5 gap-4 text-center">
          <div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{sources.revenue}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Revenue</p>
          </div>
          <div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{sources.compliance}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Compliance</p>
          </div>
          <div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{sources.workload}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Workload</p>
          </div>
          <div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{sources.meetings}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Meetings</p>
          </div>
          <div>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{sources.nps}</p>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>NPS</p>
          </div>
        </div>
      </div>

      {/* Gap Sections */}
      <div className="space-y-3">
        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Data Gaps (Sorted by Revenue)
        </h3>
        
        {gapSections.map(section => (
          <div 
            key={section.key}
            className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
          >
            <button
              onClick={() => toggleSection(section.key)}
              className={`w-full p-4 flex items-center justify-between transition-colors
                ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                  <section.icon className={`w-5 h-5 ${section.color}`} />
                </div>
                <div className="text-left">
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {section.label}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {section.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-bold
                  ${section.count > 0 
                    ? section.count > 50 
                      ? 'bg-red-500/10 text-red-500' 
                      : 'bg-amber-500/10 text-amber-500'
                    : 'bg-emerald-500/10 text-emerald-500'
                  }`}
                >
                  {section.count} plans
                </span>
                {expandedSection === section.key ? (
                  <ChevronUp className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                )}
              </div>
            </button>
            
            {expandedSection === section.key && section.data.length > 0 && (
              <div className={`border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                {/* Bulk delete toolbar */}
                {selectedForDelete.size > 0 && (
                  <div className="px-4 py-2 flex items-center justify-between bg-red-50 border-b border-red-200">
                    <span className="text-xs font-medium text-red-700">{selectedForDelete.size} plan(s) selected for removal</span>
                    <div className="flex gap-2">
                      <button
                        data-testid="clear-selection-btn"
                        onClick={() => setSelectedForDelete(new Set())}
                        className="text-xs px-2 py-1 rounded text-[#737373] hover:bg-[#F8F9FA] transition-colors"
                      >
                        Clear
                      </button>
                      <button
                        data-testid="delete-selected-btn"
                        onClick={handleDeleteSelected}
                        disabled={deleting}
                        className="text-xs px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                      >
                        {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        Remove from All Views
                      </button>
                    </div>
                  </div>
                )}
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={`sticky top-0 ${isDark ? 'bg-[#111113]' : 'bg-white'}`}>
                      <tr className={`text-[11px] uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        <th className="px-2 py-3 w-8"></th>
                        <th className="px-4 py-3 text-left font-semibold">Plan Number</th>
                        <th className="px-4 py-3 text-right font-semibold">Revenue</th>
                        <th className="px-4 py-3 text-left font-semibold">Manager</th>
                        <th className="px-2 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                      {section.data.map((plan) => (
                        <tr key={plan.planNumber} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'} ${selectedForDelete.has(plan.planNumber) ? (isDark ? 'bg-red-500/5' : 'bg-red-50') : ''}`}>
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedForDelete.has(plan.planNumber)}
                              onChange={() => toggleSelectPlan(plan.planNumber)}
                              className="w-3.5 h-3.5 rounded border-[#E5E5E5] text-red-500 focus:ring-red-500/20 cursor-pointer"
                            />
                          </td>
                          <td className={`px-4 py-2 font-mono text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            <div>{plan.planNumber}</div>
                            {plan.fy && (
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-orange-100 text-orange-700 font-sans">{plan.fy}</span>
                            )}
                          </td>
                          <td className={`px-4 py-2 text-right font-mono text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                            ${plan.revenue?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            <ManagerEditor 
                              plan={plan} 
                              isDark={isDark} 
                              onUpdate={fetchReport} 
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <button
                              data-testid={`delete-plan-${plan.planNumber}`}
                              onClick={() => { setSelectedForDelete(new Set([plan.planNumber])); }}
                              className="p-1 rounded text-[#A3A3A3] hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Remove this plan"
                            >
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {section.count > 50 && (
                  <div className={`px-4 py-2 text-xs text-center ${isDark ? 'text-slate-500 bg-white/[0.02]' : 'text-slate-400 bg-slate-50'}`}>
                    Showing top 50 by revenue. Export full report for complete list.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Excluded Plans Section */}
      {excludedPlans.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="excluded-plans-section">
          <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] bg-red-50 flex items-center justify-center">
                <Trash2 className="w-4 h-4 text-red-500" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#171717] font-heading">Excluded Plans</h3>
                <p className="text-xs text-[#737373]">{excludedPlans.length} plan(s) removed from all views. Restore to bring them back.</p>
              </div>
            </div>
          </div>
          <div className="p-4 flex flex-wrap gap-2">
            {excludedPlans.map(planNumber => (
              <div key={planNumber} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200">
                <span className="text-xs font-mono font-medium text-red-700">{planNumber}</span>
                <button
                  data-testid={`restore-plan-${planNumber}`}
                  onClick={() => handleRestorePlan(planNumber)}
                  className="p-0.5 rounded-full hover:bg-red-200 transition-colors text-red-400 hover:text-red-600"
                  title="Restore this plan"
                >
                  <RotateCcw className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CoverageCard = ({ label, value, icon: Icon, isDark }) => {
  const getColor = (v) => {
    if (v >= 90) return 'text-emerald-500';
    if (v >= 70) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className={`p-4 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${getColor(value)}`} />
        <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${getColor(value)}`}>{value}%</p>
      <div className={`mt-2 h-1.5 rounded-full ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
        <div 
          className={`h-full rounded-full transition-all ${
            value >= 90 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

export default DataQualityReport;
